import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { User } from '@core/models/user';
import { UserService } from '@core/services/user.service';

@Component({
  selector: 'app-get-started-wizard',
  templateUrl: './get-started-wizard.component.html',
  styleUrl: './get-started-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
/**
 * Cyber-themed get started wizard component for onboarding new users.
 * Provides a spectacular checklist experience with glassmorphism effects,
 * neon accents, and interactive step completion tracking.
 */
export class GetStartedWizardComponent implements OnInit {
  private userService = inject(UserService);

  progress = 0;
  progressStep = 0;
  private user!: User;
  onboardingDismissed = false;

  /**
   * Checklist steps. Each one links to the page where it is done (#943);
   * following the link also ticks it off, as clicking the step always has.
   */
  steps: Array<{
    id: number;
    title: string;
    description: string;
    icon: string;
    completed: boolean;
    route: string;
    fragment?: string;
  }> = [
    {
      id: 1,
      title: 'Create a scanner',
      description: 'Set up your first scanner',
      icon: 'fa-solid fa-satellite-dish',
      completed: false,
      route: '/dashboard/ninja-scanners',
    },
    {
      id: 2,
      title: 'Run a scan',
      description: 'Initiate your first scan',
      icon: 'fa-solid fa-search',
      completed: false,
      route: '/dashboard/first-scan',
    },
    {
      id: 3,
      title: 'Review scan results',
      description: 'Analyze your scan results',
      icon: 'fa-solid fa-chart-line',
      completed: false,
      route: '/dashboard/ninja-scanners/scans',
    },
    {
      id: 4,
      title: 'Review remediation',
      description: 'Check remediation progress',
      icon: 'fa-solid fa-tools',
      completed: false,
      route: '/dashboard/remediation-project',
    },
    {
      id: 5,
      title: 'Review assets',
      description: 'Review assets and findings',
      icon: 'fa-solid fa-server',
      completed: false,
      route: '/dashboard/ninja-scanners/assets',
    },
    {
      id: 6,
      title: 'Invite your team',
      description: 'Invite team members to help',
      icon: 'fa-solid fa-user-plus',
      completed: false,
      route: '/dashboard/company',
      fragment: 'invites',
    },
  ];

  ngOnInit(): void {
    this.userService.getUserModel().subscribe((user) => {
      if (user) {
        this.user = new User(user);
        this.progressStep = this.user.onboardingStep || 0;
        this.onboardingDismissed = this.user.onboardingDismissed || false;
        this.updateSteps();
        this.updateProgress();
      }
    });
  }

  /**
   * Update step completion status based on current progress
   */
  updateSteps() {
    this.steps.forEach((step, index) => {
      step.completed = index < this.progressStep;
    });
  }

  /**
   * Calculate and update progress percentage
   */
  updateProgress() {
    const completedSteps = this.steps.filter((step) => step.completed).length;
    // round to the nearest whole number
    this.progress = Math.round((completedSteps / this.steps.length) * 100);
  }

  /**
   * Mark a step as completed and update user progress
   * @param index Step index to complete
   */
  completeStep(index: number) {
    this.steps[index].completed = true;
    this.progressStep++;
    this.user.onboardingStep = this.progressStep;
    this.userService.update(this.user, this.user.uid).then(() => {});
    this.updateProgress();
  }

  /**
   * Following a step's link ticks it off, as clicking the step always has
   * (#943). The link itself does the navigation.
   *
   * @param index Step index.
   */
  onStepLink(index: number): void {
    if (!this.steps[index].completed) {
      this.completeStep(index);
    }
  }

  /**
   * Check if onboarding is completed
   * @returns true if onboarding is done
   */
  isOnboardingDone(): boolean {
    return this.userService.isOnboardingDone();
  }

  /**
   * Dismiss the onboarding wizard
   */
  dismissOnboarding() {
    this.user.onboardingDismissed = true;
    this.onboardingDismissed = true;
    this.userService.dismissOnboarding();
  }

  /**
   * Get progress bar width as a percentage string
   * @returns Progress percentage for CSS width
   */
  getProgressWidth(): string {
    return `${this.progress}%`;
  }

  /**
   * Get completed steps count for display
   * @returns Number of completed steps
   */
  getCompletedStepsCount(): number {
    return this.steps.filter((step) => step.completed).length;
  }
}
